import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChatCircle, Users } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import communicationService from '../../services/communicationService';
import securityService from '../../services/securityService';
import ConversationList from './ConversationList';
import ChatWindow from './ChatWindow';
import './ParentTeacherChat.css';

/**
 * ChatContainer - shared conversation/message/polling core for every 1:1
 * messaging surface in the app (teacher<->parent today; staff<->parent is
 * built on the same core). Previously this logic was independently
 * duplicated in TeacherChat.js and ParentChat.js (~85% identical) — role
 * differences are now just configuration passed in by each thin wrapper,
 * so a fix here (e.g. swapping polling for push) lands everywhere at once.
 *
 * @param {string} title - Header title, e.g. "Messages to Parents"
 * @param {string} subtitle - Header subtitle
 * @param {string} userRole - Passed through to ChatWindow for message-side styling
 * @param {string} contactNoun - Lowercase noun for the other party, e.g. "parent" / "teacher" — used in copy
 * @param {string} contextField - Which field carries the current selection context into the send payload: 'class_id' | 'student_id' | null
 * @param {*} contextValue - The current value for contextField (owned by the wrapper, e.g. selectedClass/selectedChild)
 * @param {string} recentlyMessagedStorageKey - localStorage key for the "recently messaged" list
 * @param {boolean} showChangeButton - whether to show a "Change {contactNoun}" button in the conversation header
 * @param {Function} renderSelector - ({ onSelected, onCancel, recentlyMessaged }) => JSX, renders the role-specific "start new conversation" picker
 */
const ChatContainer = ({
  title,
  subtitle,
  userRole,
  contactNoun,
  contextField = null,
  contextValue = null,
  recentlyMessagedStorageKey,
  showChangeButton = false,
  renderSelector,
}) => {
  const { user, loading: authLoading } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showSelector, setShowSelector] = useState(false);
  const [recentlyMessaged, setRecentlyMessaged] = useState(
    JSON.parse(localStorage.getItem(recentlyMessagedStorageKey)) || []
  );
  const [nameMap, setNameMap] = useState(
    JSON.parse(localStorage.getItem(`${recentlyMessagedStorageKey}NameMap`)) || {}
  );

  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [conversationPage, setConversationPage] = useState(1);
  const [messagePageMap, setMessagePageMap] = useState({});
  // fetchMessages always writes a brand-new object to messagePageMap (even
  // when the page number is unchanged), so effects below read the latest
  // value through this ref instead of depending on messagePageMap directly
  // — depending on it directly was an infinite loop: fetch -> new map
  // object -> effect re-fires because the map "changed" -> fetch again.
  const messagePageMapRef = useRef(messagePageMap);
  useEffect(() => { messagePageMapRef.current = messagePageMap; }, [messagePageMap]);

  const deduplicateConversations = useCallback((convs) => {
    if (!Array.isArray(convs) || convs.length === 0) return [];

    const seenUsers = new Map();
    convs.forEach(conv => {
      if (!conv || typeof conv !== 'object' || !conv.other_user_id) return;
      const userId = conv.other_user_id;
      if (!seenUsers.has(userId) ||
          new Date(conv.last_message_time) > new Date(seenUsers.get(userId).last_message_time)) {
        seenUsers.set(userId, conv);
      }
    });

    return Array.from(seenUsers.values())
      .sort((a, b) => new Date(b.last_message_time) - new Date(a.last_message_time));
  }, []);

  const fetchConversations = useCallback(async (page = 1) => {
    try {
      setIsLoadingConversations(true);
      setError(null);

      const data = await communicationService.getConversations(page, 20);
      let items = data.items || [];

      // Fall back to a locally-remembered name if the backend still says "Unknown"
      // (e.g. right after starting a brand-new conversation, before the next fetch).
      const storedNameMap = JSON.parse(localStorage.getItem(`${recentlyMessagedStorageKey}NameMap`)) || {};
      items = items.map(conv => {
        if (conv.other_user_name === 'Unknown' || conv.other_user_name === 'unknown') {
          const knownName = storedNameMap[conv.conversation_id] || storedNameMap[conv.other_user_id];
          if (knownName) return { ...conv, other_user_name: knownName };
        }
        return conv;
      });

      items = deduplicateConversations(items);
      setConversations(items);
      setConversationPage(page);

      if (!selectedConversation && items.length > 0) {
        setSelectedConversation(items[0].conversation_id);
      }
    } catch (err) {
      setError(`Failed to load conversations: ${err.message}`);
      console.error('Error fetching conversations:', err);
    } finally {
      setIsLoadingConversations(false);
    }
  }, [selectedConversation, deduplicateConversations, recentlyMessagedStorageKey]);

  const fetchMessages = useCallback(async (conversationId, page = 1) => {
    if (!conversationId) return;

    try {
      setIsLoadingMessages(true);
      setError(null);

      const data = await communicationService.getConversationMessages(conversationId, page, 50);
      setMessages(data.items || []);
      setMessagePageMap(prev => ({ ...prev, [conversationId]: page }));
    } catch (err) {
      setError(`Failed to load messages: ${err.message}`);
      console.error('Error fetching messages:', err);
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  const rememberContact = useCallback((id, name) => {
    setRecentlyMessaged(prev => {
      const updated = [id, ...prev.filter(x => x !== id)].slice(0, 10);
      localStorage.setItem(recentlyMessagedStorageKey, JSON.stringify(updated));
      return updated;
    });
    if (name) {
      setNameMap(prev => {
        const updated = { ...prev, [id]: name };
        localStorage.setItem(`${recentlyMessagedStorageKey}NameMap`, JSON.stringify(updated));
        return updated;
      });
    }
  }, [recentlyMessagedStorageKey]);

  const handleSelectConversation = useCallback((conversationId) => {
    setSelectedConversation(conversationId);
    setMessages([]);
    rememberContact(conversationId);
    fetchMessages(conversationId, 1);
  }, [rememberContact, fetchMessages]);

  const handleSendMessage = useCallback(async (messageData) => {
    if (!selectedConversation || !messageData.content.trim()) {
      setError(`Please select a ${contactNoun} and enter a message`);
      return;
    }

    try {
      setIsSendingMessage(true);
      setError(null);

      const payload = {
        receiver_id: selectedConversation,
        subject: messageData.subject || 'Message',
        content: messageData.content,
        message_type: 'general',
        ...(contextField ? { [contextField]: contextValue || null } : {}),
      };

      const response = await communicationService.sendMessage(payload);
      setMessages(prev => [...prev, response]);

      // Attachment upload is a separate step after the message exists (the
      // send response always has an empty attachments array). The message
      // itself already sent successfully at this point, so an attachment
      // failure here is reported on its own rather than failing the whole send.
      if (messageData.file) {
        try {
          const attachment = await communicationService.uploadAttachment(response.id, messageData.file);
          setMessages(prev => prev.map(msg =>
            msg.id === response.id
              ? { ...msg, attachments: [...(msg.attachments || []), attachment] }
              : msg
          ));
        } catch (attachErr) {
          console.error('Error uploading attachment:', attachErr);
          toast.error(`Message sent, but the attachment failed to upload: ${attachErr.message}`);
        }
      }

      setSuccessMessage('Message sent successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);

      const existingConv = conversations.find(c => c.other_user_id === selectedConversation);
      if (existingConv) {
        setConversations(prev => {
          const idx = prev.findIndex(c => c.other_user_id === selectedConversation);
          const updated = prev.map((conv, i) =>
            i === idx
              ? { ...conv, last_message: messageData.content, last_message_time: new Date().toISOString(), unread_count: 0 }
              : conv
          );
          const moved = updated[idx];
          return deduplicateConversations([moved, ...updated.filter((_, i) => i !== idx)]);
        });
      } else {
        fetchConversations(1);
      }
    } catch (err) {
      setError(`Failed to send message: ${err.message}`);
      console.error('Error sending message:', err);
    } finally {
      setIsSendingMessage(false);
    }
  }, [selectedConversation, contextField, contextValue, contactNoun, conversations, fetchConversations, deduplicateConversations]);

  const handleContactSelected = useCallback((contactId, contactName) => {
    if (!contactId) {
      setError(`Invalid ${contactNoun} selection`);
      return;
    }

    rememberContact(contactId, contactName);

    const existingConv = conversations.find(c => c.other_user_id === contactId);
    if (!existingConv) {
      const tempConversation = {
        conversation_id: contactId,
        other_user_id: contactId,
        other_user_name: contactName,
        other_user_role: userRole === 'teacher' ? 'parent' : 'teacher',
        last_message: null,
        last_message_time: new Date().toISOString(),
        unread_count: 0,
      };
      setConversations(prev => deduplicateConversations([tempConversation, ...prev]));
    }

    setSelectedConversation(contactId);
    setShowSelector(false);
    setMessages([]);
    fetchMessages(contactId, 1);
  }, [rememberContact, conversations, userRole, deduplicateConversations, fetchMessages, contactNoun]);

  const handleMarkMessageAsRead = useCallback(async (messageId) => {
    try {
      await communicationService.markMessageAsRead(messageId);
      setMessages(prev => prev.map(msg =>
        msg.id === messageId ? { ...msg, is_read: true, read_at: new Date().toISOString() } : msg
      ));
    } catch (err) {
      console.error('Error marking message as read:', err);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) fetchConversations(1);
  }, [authLoading, user, fetchConversations]);

  useEffect(() => {
    if (selectedConversation) {
      const cachedPage = messagePageMapRef.current[selectedConversation] || 1;
      fetchMessages(selectedConversation, cachedPage);
    }
    // messagePageMap intentionally excluded — see messagePageMapRef above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation, fetchMessages]);

  // Live push: a new_message SSE event (see POST /communication/messages)
  // refetches instantly instead of waiting for the fallback poll below.
  // The event carries no content, just the two participant ids, so every
  // client in the school can safely receive it — we just check whether we
  // were one of the two before reacting.
  useEffect(() => {
    if (!user?.school_id) return;
    let es;
    try {
      es = new EventSource(securityService.getEventsStreamUrl(user.school_id));
    } catch (err) {
      console.warn('SSE not available:', err);
      return;
    }

    es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type !== 'new_message') return;
        const isParticipant = data.sender_id === user.id || data.receiver_id === user.id;
        if (!isParticipant) return;

        fetchConversations(conversationPage);
        const otherParty = data.sender_id === user.id ? data.receiver_id : data.sender_id;
        if (otherParty === selectedConversation) {
          fetchMessages(selectedConversation, messagePageMapRef.current[selectedConversation] || 1);
        }
      } catch (err) {
        console.warn('Failed parsing SSE event', err);
      }
    };
    es.onerror = (err) => console.warn('Chat SSE error', err);

    return () => { try { es.close(); } catch (_) {} };
    // messagePageMap intentionally excluded — see messagePageMapRef above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.school_id, user?.id, selectedConversation, conversationPage, fetchConversations, fetchMessages]);

  // 30s reconciliation poll — new_message pushes above are the primary
  // path; this just covers a missed push (reconnect gap, etc), same
  // fallback pattern used for bus-location pushes in AdminTrackingPage.
  useEffect(() => {
    if (!selectedConversation || showSelector) return;

    const pollInterval = setInterval(async () => {
      try {
        const cachedPage = messagePageMapRef.current[selectedConversation] || 1;
        const data = await communicationService.getConversationMessages(selectedConversation, cachedPage, 50);
        setMessages(data.items || []);
      } catch (err) {
        console.error('Error polling messages:', err);
      }
    }, 30000);

    return () => clearInterval(pollInterval);
    // messagePageMap intentionally excluded — see messagePageMapRef above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation, showSelector]);

  if (authLoading) {
    return (
      <div className="chat-loading-container">
        <div className="chat-spinner"></div>
        <p>Loading chat...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="chat-error-container">
        <p>Please log in to access messages</p>
      </div>
    );
  }

  const selectedName = conversations.find(c => c.conversation_id === selectedConversation)?.other_user_name
    || nameMap[selectedConversation];

  return (
    <div className="parent-teacher-chat">
      <div className="chat-header">
        <div className="chat-header-content">
          <h1 className="chat-title">{title}</h1>
          <p className="chat-subtitle">{subtitle}</p>
        </div>
        <button className="chat-new-message-btn" onClick={() => setShowSelector(true)} title="Start a new conversation">
          + New Message
        </button>
      </div>

      {error && (
        <div className="chat-alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="alert-close">&times;</button>
        </div>
      )}
      {successMessage && (
        <div className="chat-alert alert-success">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="alert-close">&times;</button>
        </div>
      )}

      <div className="chat-container">
        <div className="chat-sidebar">
          <ConversationList
            conversations={conversations}
            selectedConversation={selectedConversation}
            onSelectConversation={handleSelectConversation}
            isLoading={isLoadingConversations}
            onLoadMore={() => fetchConversations(conversationPage + 1)}
          />
        </div>

        <div className="chat-content">
          {showSelector ? (
            renderSelector({
              onSelected: handleContactSelected,
              onCancel: () => setShowSelector(false),
              recentlyMessaged,
            })
          ) : selectedConversation ? (
            <>
              {showChangeButton && (
                <div className="conversation-header">
                  <div className="conversation-header-left">
                    <h2 className="conversation-title">{selectedName || 'Conversation'}</h2>
                  </div>
                  <button className="conversation-change-btn" onClick={() => setShowSelector(true)} title={`Select a different ${contactNoun}`}>
                    <Users size={18} /> Change {contactNoun}
                  </button>
                </div>
              )}
              <ChatWindow
                conversationId={selectedConversation}
                messages={messages}
                onSendMessage={handleSendMessage}
                onMarkAsRead={handleMarkMessageAsRead}
                isLoading={isLoadingMessages}
                isSending={isSendingMessage}
                userRole={userRole}
              />
            </>
          ) : (
            <div className="chat-empty-state">
              <div className="empty-state-icon"><ChatCircle size={48} /></div>
              <h2>No Conversations Yet</h2>
              <p>Start a new conversation by selecting a {contactNoun}</p>
              <button className="chat-empty-action-btn" onClick={() => setShowSelector(true)}>
                Start New Conversation
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatContainer;
